import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

/**
 * Closes a dialog when the page underneath it navigates.
 *
 * Dialogs that contain links (quick view, cart Quick Edit) used to wire
 * `onClick={onClose}` onto each link by hand; every link added later
 * re-introduced the "modal stays mounted over the destination" bug
 * (COM-104). The real invariant is "an open dialog closes on any
 * navigation", so mount this once in the dialog shell instead.
 *
 * Keys on `pathname + search`, not `location.key`, so a `replace` to the
 * same URL or a hash change doesn't close the dialog. Only reacts while
 * `active` (the dialog's `open` prop): while closed it just tracks the
 * current URL so re-opening doesn't fire a stale close.
 *
 * Note this fires when the navigation COMPLETES (the URL changes), so a
 * link that should close the dialog immediately on click still passes its
 * own click handler — see `renderBrandRow`'s `onNavigate`.
 */
export function useCloseOnNavigation(onClose: () => void, active = true) {
  const { pathname, search } = useLocation();
  const href = pathname + search;
  const lastHref = useRef(href);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) {
      lastHref.current = href;
      return;
    }
    if (href !== lastHref.current) {
      lastHref.current = href;
      onCloseRef.current();
    }
  }, [href, active]);
}
